import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LabShell, { LabNotifyForm, NotifyDialog, useLabPlays } from "@/components/LabShell";
import Staff from "@/components/Staff";
import LetterStrip from "@/components/LetterStrip";
import HebrewKeyboard from "@/components/HebrewKeyboard";
import AboutExperiment from "@/components/AboutExperiment";
import { buildHebrewSequence } from "@/lib/hebrewComposition";
import { unlockAudio } from "@/lib/synth";
import { playSequenceAudio } from "@/lib/playEngine";
import { pickRandomHebrewPhrase } from "@/lib/hebrewWords";
import { tokenizeHebrew } from "@/lib/hebrewCryptogram";
import { measureSequence } from "@/lib/measures";
import { defaultBpmFor, stepMsForBpm } from "@/lib/presets";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One key, one feel: the demo is locked to measured Groove — bars, rests, pulse.
const DEMO_SCALE = { root: "G", modeIdx: 0, scale: "major", condensed: false };
const FEEL = "groove";
const DEMO_BPM = defaultBpmFor(FEEL);
const DEMO_STEP_MS = stepMsForBpm(FEEL, DEMO_BPM);
const MAX_LETTERS = 60;
const MAX_CHUMASH_WORDS = 10;

const CHAPTERS = [
  {
    title: "The letters are characters",
    paras: [
      "Not symbols. Not decoration. Characters — each with a personality, a story, an energy and a job in creation. Aleph isn't a stand-in for the sound \u201Ca\u201D; aleph is someone.",
      "Learn the twenty-two of them and you stop reading Hebrew as code. You start reading it as a cast list.",
    ],
  },
  {
    title: "Words are stories",
    paras: [
      "Put characters together and you don't get a label, you get a scene: who's present, in what order, doing what to whom. The word is the plot.",
      "This is why two words that translate the same into English don't feel the same in Hebrew. Different cast, different scene.",
    ],
  },
  {
    title: "The mapping",
    paras: [
      "The Alephonic Mapping Method\u2122 is the system Dan built over years of learning — a fixed set of rules that takes the letters of a Hebrew word and produces musical material from them. It isn't a mood, an interpretation or a vibe. It's a method, and it works the same way every time.",
      "Here's the part anyone can verify on this page: each letter sits on its own degree of the scale, climbing as the alphabet climbs. The order of the letters sets the order of the notes; the shape of the word sets the shape of the phrase. Run the same word twice and you get the same music twice — that's what makes it a system rather than a feeling.",
      "Which degrees, which modes, how phrasing and rhythm are derived, and what happens when letters interact — that's the proprietary part, and it stays in-house. What you're playing here is the plainest possible view of it: the twenty-two letters, in order, as notes, in G major.",
    ],
  },
  {
    title: "Why it's measured here",
    paras: [
      "This demo is locked to what Dan calls measured Groove. Every letter lands as an eighth note on a real metrical grid, short words share a bar, longer words start on a bar line and the leftover beats are written out as rests — which is why you'll see bar lines and rest glyphs appear as the melody drops in.",
      "That matters because a word isn't only a sequence of pitches. It has a length, and the silence after it is part of the phrase. Measured mode keeps that silence honest, so what you see on the staff is exactly what a musician could pick up and play.",
    ],
  },
  {
    title: "The improvisation",
    paras: [
      "The map gives structure. It doesn't give breath. Dan sits at the piano and plays what the letters laid out — voicing it, weighting it, deciding where to linger and where to let it go.",
      "That's the part no machine does, and it's why two performances of the same word are two different performances. Twenty-two notes on a staff is the skeleton; a person is what makes it music.",
    ],
  },
  {
    title: "Why music, and why this exists",
    paras: [
      "Music is the pen of the soul. It reaches places words are too slow and too literal to get to — which is exactly why it's the right instrument for letters that were never only sounds in the first place.",
      "Creativity isn't decoration on top of a life of learning. It's a gift, given deliberately, and using it is a form of access. When you make something you're doing a small version of what was done to make everything.",
      "So the point was never to explain a verse more cleverly. It's to be inside it. Playing a word is a way of standing in it instead of looking at it from the outside, and there are things you can only know about a word by hearing it. That's the whole reason this page is here, free, with nothing to sign.",
    ],
  },
];

export default function AlephonicsDemo() {
  usePageMeta({
    title: "Alephonics: Translate Hebrew Into Music",
    description:
      "Type any Hebrew word — your name, a blessing, today's Chumash — and watch the melody hiding inside it land on the staff, note by note.",
    path: "/alephonics",
  });
  const [text, setText] = useState("");
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loadingChumash, setLoadingChumash] = useState(false);
  const [source, setSource] = useState(null);
  const [dropped, setDropped] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [playedKeys, setPlayedKeys] = useState(() => new Set());
  const [activeKey, setActiveKey] = useState(null);
  const [previewWordIdx, setPreviewWordIdx] = useState(-1);
  const runRef = useRef(0);
  const { countPlay, askNotify, dismissNotify } = useLabPlays();

  const words = useMemo(() => text.split(/\s+/).filter(Boolean), [text]);
  const letterCount = useMemo(
    () => words.reduce((a, w) => a + tokenizeHebrew(w).length, 0),
    [words]
  );
  const tooLong = letterCount > MAX_LETTERS;
  const sequence = useMemo(() => buildHebrewSequence(words, DEMO_SCALE), [words]);
  const measuredSteps = useMemo(
    () => (sequence.length ? measureSequence(sequence).steps : []),
    [sequence]
  );

  const reset = () => {
    runRef.current += 1;
    setDropped([]);
    setActiveIndex(-1);
    setPlayedKeys(new Set());
    setActiveKey(null);
    setPreviewWordIdx(-1);
    setPlaying(false);
  };

  const setPhrase = (value, src = null) => {
    setText(value);
    setSource(src);
    reset();
  };

  // Today's Chumash, straight from Sefaria — the opening of today's aliyah,
  // trimmed to something the staff can hold.
  const loadChumash = async () => {
    if (loadingChumash) return;
    setLoadingChumash(true);
    try {
      const { data } = await axios.get(`${API}/torah/daily`, {
        params: { weekday: new Date().getDay() },
      });
      const chumash = (data.sources || []).find((s) => s.key === "chumash");
      if (!chumash?.hebrew) throw new Error("no chumash");
      let picked = chumash.hebrew.split(/\s+/).filter(Boolean).slice(0, MAX_CHUMASH_WORDS);
      while (
        picked.length > 1 &&
        picked.reduce((a, w) => a + tokenizeHebrew(w).length, 0) > MAX_LETTERS
      ) {
        picked = picked.slice(0, -1);
      }
      setPhrase(picked.join(" "), { label: chumash.display || "Today's Chumash", ref: chumash.ref });
    } catch {
      toast.error("Couldn't reach Sefaria just now — try Surprise Me.");
    } finally {
      setLoadingChumash(false);
    }
  };

  const play = async () => {
    if (playing || sequence.length === 0 || tooLong) return;
    unlockAudio();
    countPlay();
    const run = runRef.current + 1;
    runRef.current = run;
    setPlaying(true);
    setDropped([]);
    setPlayedKeys(new Set());
    const done = new Set();
    const collected = [];
    await playSequenceAudio(measuredSteps, {
      presetKey: FEEL,
      measured: true,
      bpm: () => DEMO_BPM,
      pedal: false,
      shouldStop: () => runRef.current !== run,
      onTick: (i, entry, stepMs) => {
        if (runRef.current !== run || entry.rest) return;
        collected.push({ ...entry, stepMs });
        setDropped([...collected]);
        setActiveIndex(collected.length - 1);
        done.add(`${entry.wordIdx}:${entry.charIdx}`);
        setPlayedKeys(new Set(done));
        setActiveKey(`${entry.wordIdx}:${entry.charIdx}`);
      },
    });
    if (runRef.current !== run) return;
    setPlaying(false);
    setActiveIndex(-1);
    setActiveKey(null);
  };

  // Tap a word in the strip and just that word plays again. If the piece is
  // already on the staff those notes get the preview wash; if it isn't, the
  // word drops in on its own.
  const playWord = async (wordIdx) => {
    const wordSeq = sequence.filter((e) => e.wordIdx === wordIdx);
    if (playing || wordSeq.length === 0) return;
    unlockAudio();
    const run = runRef.current + 1;
    runRef.current = run;
    const onStaff = dropped.length > 0;
    setPlaying(true);
    setPreviewWordIdx(onStaff ? wordIdx : -1);
    const collected = [];
    if (!onStaff) setDropped([]);
    const done = new Set(playedKeys);
    await playSequenceAudio(wordSeq, {
      presetKey: FEEL,
      bpm: () => DEMO_BPM,
      pedal: false,
      shouldStop: () => runRef.current !== run,
      onTick: (i, entry, stepMs) => {
        if (runRef.current !== run) return;
        const key = `${entry.wordIdx}:${entry.charIdx}`;
        done.add(key);
        setPlayedKeys(new Set(done));
        setActiveKey(key);
        if (onStaff) return;
        collected.push({ ...entry, stepMs });
        setDropped([...collected]);
        setActiveIndex(collected.length - 1);
      },
    });
    if (runRef.current !== run) return;
    setPlaying(false);
    setActiveIndex(-1);
    setActiveKey(null);
    setPreviewWordIdx(-1);
  };

  return (
    <LabShell wide>
      <Toaster position="bottom-right" richColors />
      <NotifyDialog open={askNotify} onClose={dismissNotify} />

      <section className="pt-10 sm:pt-14 pb-6">
        <p className="text-xs uppercase mb-3" style={{ color: "#8C8198", fontFamily: "'Courier Prime', monospace", letterSpacing: "0.2em" }}>
          Experiment 02
        </p>
        <h1 className="text-4xl sm:text-5xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }} data-testid="alephonics-demo-title">
          Alephonics: Translate Hebrew Into Music
        </h1>
        <p className="mt-3 text-sm sm:text-base max-w-[64ch]" style={{ color: "#5C5268", lineHeight: 1.75 }}>
          Every letter of the aleph-bet gets its own note. Type a word — your name, a blessing,
          the first line of the parsha — and watch the melody hiding inside it land on the
          staff in bars and rests, one note at a time.
        </p>
      </section>

      <section className="rounded-2xl p-4 sm:p-5 mb-5" style={{ backgroundColor: "#FFFFFF", border: "1px solid #EBE3D5" }}>
        <Input
          data-testid="alephonics-demo-input"
          value={text}
          onChange={(e) => setPhrase(e.target.value)}
          dir="rtl"
          placeholder="בראשית"
          className="h-12 text-xl bg-[#FFFBF2] border-[#D6CBB8] text-[#1A1523] placeholder:text-[#8C8198]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        />
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <Button
            data-testid="alephonics-demo-inspire"
            onClick={() => setPhrase(pickRandomHebrewPhrase(5, 18))}
            className="h-11 rounded-full px-5 bg-[#FEF3D6] hover:bg-[#F7E7BD] text-[#9A6E12]"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.12em" }}
          >
            ✦ Surprise Me
          </Button>
          <Button
            data-testid="alephonics-demo-chumash"
            onClick={loadChumash}
            disabled={loadingChumash}
            className="h-11 rounded-full px-5 bg-[#FFF0EA] hover:bg-[#FBE2D7] text-[#B2502F]"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.12em" }}
          >
            {loadingChumash ? "Loading…" : "Today's Chumash"}
          </Button>
          <Button
            data-testid="alephonics-demo-keyboard"
            onClick={() => setShowKeyboard((v) => !v)}
            className="h-11 rounded-full px-5 bg-[#E3F3F5] hover:bg-[#D2EBEE] text-[#15545F]"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.12em" }}
          >
            {showKeyboard ? "Hide keyboard" : "Hebrew Keyboard"}
          </Button>
          <div className="sm:ml-auto flex gap-2">
            <Button
              data-testid="alephonics-demo-play"
              onClick={play}
              disabled={playing || sequence.length === 0 || tooLong}
              className="h-11 rounded-full px-7 bg-[#1B6C7A] hover:bg-[#15545F] text-white"
              style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
            >
              {playing ? "PLAYING…" : "PLAY"}
            </Button>
            <Button
              data-testid="alephonics-demo-reset"
              onClick={reset}
              className="h-11 rounded-full px-5 bg-white hover:bg-[#FFFDF7] text-[#5C5268]"
              style={{ border: "1px solid #D6CBB8", fontFamily: "'Courier Prime', monospace", letterSpacing: "0.18em" }}
            >
              RESET
            </Button>
          </div>
        </div>
        <p className="mt-3 text-xs" style={{ color: tooLong ? "#E86F4A" : "#8C8198" }} data-testid="alephonics-demo-counter">
          {tooLong
            ? `This demo takes up to ${MAX_LETTERS} letters — trim it a little.`
            : `${letterCount} / ${MAX_LETTERS} letters · separate words with spaces`}
        </p>
        {source && (
          <p className="mt-1 text-xs" style={{ color: "#9A6E12", fontFamily: "'Courier Prime', monospace" }} data-testid="alephonics-demo-source">
            {source.label}
            {source.ref ? ` · ${source.ref}` : ""}
          </p>
        )}
      </section>

      {showKeyboard && (
        <div className="mb-5">
          <HebrewKeyboard
            theme="light"
            onLetter={(ch) => setPhrase(text + ch)}
            onSpace={() => setPhrase(`${text} `)}
            onBackspace={() => setPhrase([...text].slice(0, -1).join(""))}
            disabled={playing}
          />
        </div>
      )}

      <section className="pb-4">
        <LetterStrip
          testId="alephonics-letter-strip"
          theme="light"
          sequence={sequence}
          playedKeys={playedKeys}
          activeKey={activeKey}
          onWordClick={playing ? null : playWord}
          hint="Type a Hebrew word above — its letters show up here."
        />
      </section>

      <section className="pb-8">
        <Staff
          droppedEntries={dropped}
          reserveEntries={sequence}
          activeIndex={activeIndex}
          previewingWordIdx={previewWordIdx}
          previewingKey={previewWordIdx === -1 ? null : activeKey}
          baseStepMs={DEMO_STEP_MS}
          measuredSteps={measuredSteps.length ? measuredSteps : null}
          noteStyle="groove"
          theme="light"
        />
      </section>

      <section className="pb-14 grid gap-6 md:grid-cols-[1.3fr_1fr]">
        <div className="rounded-2xl p-6" style={{ backgroundColor: "#FFFBF2", border: "1px solid #EBE3D5" }}>
          <h2 className="text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Want the next experiment?
          </h2>
          <p className="text-sm mb-4" style={{ color: "#5C5268", lineHeight: 1.7 }}>
            Leave your name and we&apos;ll let you know when something new opens in the lab.
          </p>
          <LabNotifyForm testPrefix="alephonics-demo-notify" />
        </div>
        <div className="rounded-2xl p-6" style={{ backgroundColor: "#FEF3D6", border: "1px solid #EBE3D5" }}>
          <h2 className="text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Try the harp
          </h2>
          <p className="text-sm mb-4" style={{ color: "#9A6E12", lineHeight: 1.7 }}>
            Same letters, twenty-two strings you can pluck yourself.
          </p>
          <Link
            to="/harp"
            data-testid="alephonics-demo-cross-link"
            className="inline-block rounded-full px-5 py-2.5 text-xs"
            style={{ backgroundColor: "#D99B26", color: "#fff", fontFamily: "'Courier Prime', monospace", letterSpacing: "0.16em" }}
          >
            PLAY DAVID&apos;S HARP →
          </Link>
        </div>
      </section>

      <AboutExperiment
        testPrefix="alephonics-about"
        lede="Alephonics isn't a gimmick that assigns random notes to letters. It's the public, pared-back face of the Alephonic Mapping Method™ — the system Dan Berry built over years of learning and uses when he turns a family's names, a community's name or a line of Torah into a piece of music. Here's how it works, and why it exists at all."
        chapters={CHAPTERS}
      />
    </LabShell>
  );
}
